'use client';

import React from 'react';
import { cn } from '@/utils';
import { Icon } from '@/components';

export interface INativeSelectOption {
  /**
   * The value of the option
   */
  value: string;
  /**
   * The display label for the option
   */
  label: string;
  /**
   * Whether the option is disabled
   */
  disabled?: boolean;
}

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /**
   * Array of options for the select dropdown
   */
  options: INativeSelectOption[];

  /**
   * Currently selected value
   */
  value?: string;

  /**
   * Label for the select field
   */
  label?: string;

  /**
   * Placeholder text when no option is selected
   */
  placeholder?: string;

  /**
   * Whether the select is disabled
   */
  disabled?: boolean;

  /**
   * Error message to display, or true for error state
   */
  error?: string | boolean;

  /**
   * Whether the select is in a loading state
   */
  isLoading?: boolean;

  /**
   * Unique identifier for the select
   */
  id?: string;

  /**
   * Name attribute for form integration
   */
  name?: string;

  /**
   * ARIA label for accessibility
   */
  'aria-label'?: string;

  /**
   * Whether to show the selected option's label as the value in the select trigger.
   * If false, the trigger will always show the placeholder text, even when an option
   * is selected. This can be useful in cases where you want to indicate that a
   * selection has been made without showing the specific option in the trigger.
   *
   * Defaults to true.
   */
  showSelectionAsValue?: boolean;

  /**
   * Callback function that is called when the selected value changes.
   * Receives the new value as an argument.
   */
  onChangeCallback?: (value: string) => void;
}

/**
 * Select Component
 *
 * A dropdown select component that matches the Figma design with proper
 * accessibility features and keyboard navigation.
 */
export const NativeSelect: React.FC<SelectProps> = ({
  options,
  value,
  label,
  placeholder = 'Select an option',
  disabled = false,
  error = false,
  isLoading = false,
  className,
  id,
  name,
  'aria-label': ariaLabel,
  showSelectionAsValue = true,
  onChangeCallback,
  ...props
}) => {
  // Base styles matching Figma design
  const selectClasses = cn(
    'relative inline-flex items-center justify-between',
    'border border-neutral-gray-400 rounded-lg',
    'px-3 py-2 gap-3',
    'font-text text-button-small font-bold leading-inherit',
    'text-semantic-text-primary',
    'cursor-pointer select-none',
    'transition-colors duration-200',
    'min-w-40 w-full',
    'z-10',
    {
      'cursor-not-allowed': disabled,
      'border-system-error': !!error && !disabled,
    },
    className,
  );

  return (
    <div className="space-y-1">
      {label && (
        <label
          htmlFor={id}
          className="block font-text text-body font-medium leading-body text-semantic-text-primary"
        >
          {label}
        </label>
      )}

      <div className="relative w-full">
        <select
          aria-label={ariaLabel}
          className={cn(selectClasses, 'appearance-none')}
          disabled={disabled || isLoading}
          id={id}
          name={name}
          onChange={onChangeCallback ? (e) => onChangeCallback(e.target.value) : undefined}
          value={value}
          {...props}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>

        <div
          className={cn(
            'absolute bottom-0 left-auto right-0 top-0 z-10',
            'flex h-full items-center justify-center',
            'pointer-events-none px-3',
          )}
        >
          <Icon name="CaretDown" size={16} />
        </div>
      </div>
    </div>
  );
};

NativeSelect.displayName = 'NativeSelect';

export default NativeSelect;
